// Map distribution body — donut + bar visualizations of pair count or
// numeric value sum per key. One parameterized component, exported four
// times so the chart registry can wire each combination to its own
// chart_id.
//
// All four variants share the fetch, the time-range picker, the bucket
// aggregation (via mapAggregation.ts), and the color palette. They
// differ only in:
//   * `mode` — 'count' counts checkin_features rows per key;
//     'sum' adds up decimal_value per key (numeric values only).
//   * `viz`  — 'pie' renders a donut with a Legend; 'bar' renders a
//     vertical bar chart with angled X labels.
//
// The sum charts only enter the cycle for map_numeric features (see
// VALUE_TYPE_CATALOG); for non-numeric maps the cycle skips them.

import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Cell, Tooltip, ResponsiveContainer,
  PieChart, Pie, Sector, Legend,
} from 'recharts';
import { api } from '../../api/client';
import type { ChartBodyProps } from '../types';
import { useTimeRangeState, windowForAnchor } from '../windowRange';
import ChartTimeRangePicker from '../ChartTimeRangePicker';
import {
  aggregateMapRows,
  type AggregationMode,
  type MapValueRow,
} from './mapAggregation';
import {
  BAR_CATEGORY_GAP, BAR_TICK, BAR_TOOLTIP_CURSOR, BarTooltipCard, barChartMargin,
} from './barChartStyles';

type Viz = 'pie' | 'bar';

interface MapDistributionBodyProps extends ChartBodyProps {
  mode: AggregationMode;
  viz: Viz;
}

const MapDistributionBody: React.FC<MapDistributionBodyProps> = ({
  featureId, unit, fullscreen, mode, viz,
}) => {
  const [rows, setRows] = useState<MapValueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<MapValueRow[]>(`/api/features/values/${featureId}`)
      .then((resp) => { if (!cancelled) { setRows(resp.data); setError(null); } })
      .catch((err) => {
        console.error('Failed to fetch feature values:', err);
        if (!cancelled) setError('Failed to load distribution data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  // Only include map rows (those with a resolved key_label) in the
  // time-range data set. A non-map row in the response would skew the
  // anchor toward dates that don't actually contribute to the chart.
  const dataDates = useMemo(
    () => rows.filter((r) => r.key_label != null).map((r) => new Date(r.date).getTime()),
    [rows],
  );
  const range = useTimeRangeState(dataDates);
  const win = useMemo(
    () => windowForAnchor(range.granularity, range.anchor),
    [range.granularity, range.anchor],
  );

  const buckets = useMemo(() => aggregateMapRows(rows, mode, win), [rows, mode, win]);
  const total = buckets.reduce((acc, b) => acc + b.value, 0);

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (dataDates.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No pairs recorded yet for this feature.</p>
      </div>
    );
  }

  const formatNumber = (n: number) =>
    Number.isInteger(n) ? n.toString() : n.toFixed(2);

  const renderTooltipValue = (name: string, value: number) => {
    if (mode === 'count') {
      const pct = total > 0 ? Math.round((value / total) * 100) : 0;
      return `${value} pair${value === 1 ? '' : 's'} (${pct}%)`;
    }
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    const label = unit ? `${formatNumber(value)} ${unit}` : formatNumber(value);
    return `${label} (${pct}%)`;
    void name; // kept for symmetry with future per-bucket coloring
  };

  return (
    <div className={fullscreen ? 'h-full flex flex-col' : 'flex flex-col'} style={{ touchAction: 'none' }}>
      <ChartTimeRangePicker state={range} />
      <div className={fullscreen ? 'flex-1 min-h-0' : 'h-80'}>
        {total === 0 ? (
          <div className="flex items-center justify-center h-full text-center text-gray-500">
            <p>{mode === 'sum' ? 'No numeric values in this period.' : 'No pairs in this period.'}</p>
          </div>
        ) : viz === 'pie' ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={buckets}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="40%"
                outerRadius="75%"
                paddingAngle={1}
                isAnimationActive={true}
                activeShape={(props: {
                  cx: number; cy: number; innerRadius: number; outerRadius: number;
                  startAngle: number; endAngle: number; fill: string;
                }) => (
                  <Sector
                    cx={props.cx}
                    cy={props.cy}
                    innerRadius={props.innerRadius}
                    outerRadius={props.outerRadius}
                    startAngle={props.startAngle}
                    endAngle={props.endAngle}
                    fill={props.fill}
                    fillOpacity={0.5}
                    stroke="#ffffff"
                    strokeWidth={3}
                  />
                )}
              >
                {buckets.map((b) => <Cell key={b.name} fill={b.color} style={{ outline: 'none' }} />)}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const value = payload[0].value as number;
                  const name = payload[0].name as string;
                  return (
                    <div className="bg-white border border-gray-200 rounded shadow-md px-2 py-1 text-sm text-gray-900">
                      {name}: {renderTooltipValue(name, value)}
                    </div>
                  );
                }}
              />
              <Legend align="left" />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={buckets}
              margin={barChartMargin(!!fullscreen, 'categorical-angled')}
              barCategoryGap={BAR_CATEGORY_GAP}
            >
              <XAxis
                dataKey="name"
                tick={BAR_TICK}
                angle={-30}
                textAnchor="end"
                interval={0}
                height={fullscreen ? 70 : 50}
                tickLine={false}
              />
              <YAxis allowDecimals={mode === 'sum'} tick={BAR_TICK} tickLine={false} />
              <Tooltip
                cursor={BAR_TOOLTIP_CURSOR}
                content={({ active, payload }: { active?: boolean; payload?: Array<{ value: number; payload: { name: string } }> }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const value = payload[0].value;
                  const name = payload[0].payload?.name ?? '';
                  return (
                    <BarTooltipCard>
                      {name && <div className="text-gray-600">{name}</div>}
                      <div className="text-gray-900">{renderTooltipValue(name, value)}</div>
                    </BarTooltipCard>
                  );
                }}
              />
              <Bar dataKey="value" isAnimationActive={true}>
                {buckets.map((b) => <Cell key={b.name} fill={b.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export const MapSumPieBody: React.FC<ChartBodyProps> = (props) => (
  <MapDistributionBody {...props} mode="sum" viz="pie" />
);
export const MapCountPieBody: React.FC<ChartBodyProps> = (props) => (
  <MapDistributionBody {...props} mode="count" viz="pie" />
);
export const MapCountBarBody: React.FC<ChartBodyProps> = (props) => (
  <MapDistributionBody {...props} mode="count" viz="bar" />
);

